import { useEntitySchemaByName } from "@/hooks/schema/use-entity-schema-by-name";
import { useEffect } from "react";
import { create } from "zustand";
import { useEntityStore } from "./entity.store";
import { getDefaultField } from "@/lib/utils";

export interface EntityInstanceLayoutState {
  field: string;
}

// Define the Zustand store shape
interface EntityInstanceStore {
  eilStates: Record<string, EntityInstanceLayoutState>;
  updateEilState: (entityName: string, newState: Partial<EntityInstanceLayoutState>) => void;
  resetEilState: (entityName: string) => void;
}

// Zustand store implementation
export const useFieldNameStore = create<EntityInstanceStore>((set) => ({
  eilStates:
    typeof window !== "undefined" ? JSON.parse(localStorage.getItem("eilStates") || "{}") : {},

  updateEilState: (entityName, newState) =>
    set((state) => {
      const updatedStates = {
        ...state.eilStates,
        [entityName]: {
          ...state.eilStates[entityName],
          ...newState,
        },
      };
      if (typeof window !== "undefined") {
        localStorage.setItem("eilStates", JSON.stringify(updatedStates));
      }
      return { eilStates: updatedStates };
    }),

  resetEilState: (entityName) =>
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [entityName]: _, ...rest } = state.eilStates;
      if (typeof window !== "undefined") {
        localStorage.setItem("eilStates", JSON.stringify(rest));
      }
      return { eilStates: rest };
    }),
}));

export const useEntityInstanceLayoutStore = (entityName: string) => {
  const { entitySchema } = useEntitySchemaByName(entityName);
  const { getEntityByName } = useEntityStore();
  const updateState = useFieldNameStore((state) => state.updateEilState);
  const state = useFieldNameStore((state) => state.eilStates[entityName]) ?? {
    field: "",
  };
  const resetState = useFieldNameStore((state) => state.resetEilState);

  useEffect(() => {
    const entity = getEntityByName(entityName);
    const entityFields = entity?.entity_fields?.data ?? [];

    // Wait until entity is loaded
    if (!entity || entityFields.length === 0) {
      return;
    }

    // Check if current field exists in the entity's fields. A dotted value is a
    // deep relation path (e.g. `customer.region.name`) produced by the field
    // picker — it won't match a flat field name, so trust it rather than
    // resetting (which caused the selection to "jump back" to the default).
    const isRelationPath = state.field.includes(".");
    const fieldExists =
      state.field !== "" && (isRelationPath || entityFields.some((f) => f.name === state.field));

    if (state.field === "" || !fieldExists) {
      // Field is empty or no longer exists - get a valid default
      const initField = getDefaultField(entityName, entityFields) || "unknown";
      updateState(entityName, { field: initField });
    }
  }, [state.field, updateState, entityName, getEntityByName]);

  return {
    state,
    entitySchema,
    setState: (state: Partial<EntityInstanceLayoutState>) => {
      updateState(entityName, state);
    },
    resetState,
  };
};
